import { t } from '@procumi/shared';
import type {
  UserSignature,
  Account,
  AltDecision,
  BoqExtraction,
  Billing,
  PlanId,
  PlanFit,
  PlanUsage,
  OnboardingFacts,
  OrderRow,
  ApprovalSettings,
  Company,
  CompanyInput,
  DashboardData,
  DeliveryInput,
  ExtractInput,
  FxRate,
  LoginInput,
  OfferExtraction,
  OfferInput,
  PriceMarket,
  PricePeriod,
  PublicOffer,
  PublicOfferInput,
  SupplierProfileInput,
  RfqBench,
  PriceQuote,
  Rfq,
  SiteDocument,
  ActivityEvent,
  RfqChoiceInput,
  InviteCreated,
  InviteInfo,
  ResetInfo,
  SignupStartInput,
  SignupStarted,
  RfqInput,
  RfqListItem,
  RfqPage,
  RfqQuery,
  SendFileUpload,
  PoDraft,
  ReqGroup,
  Requisition,
  SiteOrder,
  RequisitionInput,
  SiteRef,
  PoKind,
  PoInput,
  Site,
  SiteLedger,
  SiteInput,
  SitePatch,
  Supplier,
  AnafInfo,
  NotificationList,
  SupplierInput,
  SupplierPatch,
  SupplierRemoval,
  SupplierScore,
  Team,
  TodayRfq,
  InboxList,
  InboxMail,
  InboxOption,
  AgreedPrice,
  OrderList,
  MyTicket,
} from '@procumi/shared';

import { lang } from './i18n';

const BASE = '/api';

/** Sesiunea a expirat sau lipsește: aplicația se întoarce la ecranul de intrare. */
export const UNAUTHORIZED = 'app:unauthorized';

async function req<T>(path: string, init?: RequestInit): Promise<T> {
  let res: Response;
  // La multipart, Content-Type îl pune browserul — cu boundary-ul lui, pe care
  // noi nu-l putem scrie. Deci la FormData nu trimitem antetul deloc.
  const form = init?.body instanceof FormData;
  try {
    res = await fetch(BASE + path, {
      credentials: 'same-origin',
      headers: { ...(form ? {} : { 'Content-Type': 'application/json' }), 'X-Lang': lang() },
      ...init,
    });
  } catch {
    throw new Error(t('Serverul nu răspunde. Verifică dacă API-ul rulează.'));
  }
  if (res.status === 401) {
    window.dispatchEvent(new CustomEvent(UNAUTHORIZED));
  }
  if (!res.ok) {
    let message = t('Ceva nu a funcționat.');
    try {
      const body = (await res.json()) as { error?: string };
      if (body?.error) message = body.error;
    } catch {
      /* răspuns fără JSON */
    }
    throw new Error(message);
  }
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

const json = (body: unknown): RequestInit['body'] => JSON.stringify(body);

export const api = {
  me: () => req<Account>('/auth/me'),
  /** pagina furnizorului, din linkul de ofertă: fără cont */
  publicOffer: (token: string) => req<PublicOffer>(`/public/oferta/${encodeURIComponent(token)}`),
  /** Datele firmei, trecute de furnizor după ofertă. */
  saveSupplierProfile: (token: string, body: SupplierProfileInput) =>
    req<PublicOffer>(`/public/oferta/${encodeURIComponent(token)}/firma`, {
      method: 'PUT',
      body: json(body),
    }),
  /** Oferta ca fișier (PDF, Excel, poză), încărcată de furnizor pe linkul lui; o citește Atlas. */
  uploadPublicOffer: (token: string, file: File) => {
    const form = new FormData();
    form.set('file', file);
    return req<PublicOffer>(`/public/oferta/${encodeURIComponent(token)}/document`, { method: 'POST', body: form });
  },
  submitPublicOffer: (token: string, body: PublicOfferInput) =>
    req<PublicOffer>(`/public/oferta/${encodeURIComponent(token)}`, {
      method: 'PUT',
      body: json(body),
    }),
  signupInfo: (token: string) => req<{ ok: true }>(`/auth/signup/${encodeURIComponent(token)}`),
  signupStart: (body: SignupStartInput) =>
    req<SignupStarted>('/auth/signup/start', { method: 'POST', body: json(body) }),
  signupVerify: (body: { token: string; code: string }) =>
    req<Account>('/auth/signup/verify', { method: 'POST', body: json(body) }),
  login: (body: LoginInput) => req<Account>('/auth/login', { method: 'POST', body: json(body) }),
  forgotPassword: (email: string) =>
    req<void>('/auth/forgot-password', { method: 'POST', body: json({ email }) }),
  resetInfo: (token: string) => req<ResetInfo>(`/auth/reset/${encodeURIComponent(token)}`),
  resetPassword: (body: { token: string; password: string }) =>
    req<Account>('/auth/reset-password', { method: 'POST', body: json(body) }),
  logout: () => req<void>('/auth/logout', { method: 'POST' }),

  company: () => req<Company>('/company'),
  updateCompany: (body: CompanyInput) => req<Company>('/company', { method: 'PATCH', body: json(body) }),
  planUsage: () => req<PlanUsage>('/company/plan/usage'),
  planFit: () => req<PlanFit | null>('/company/plan/fit'),
  fitPlan: (body: { keepUsers: string[]; keepSites: string[] }) =>
    req<PlanFit | null>('/company/plan/fit', { method: 'POST', body: json(body) }),
  onboarding: () => req<OnboardingFacts>('/company/onboarding'),
  approvals: () => req<ApprovalSettings>('/company/approvals'),
  saveApprovals: (body: {
    steps: { minValue: number; approver: string }[];
    sites: Record<string, string | null>;
  }) => req<ApprovalSettings>('/company/approvals', { method: 'PUT', body: json(body) }),
  hideOnboarding: () => req<Company>('/company/onboarding/hide', { method: 'POST' }),
  requestPlan: (body: { plan: PlanId; billing: Billing }) =>
    req<Company>('/company/plan', { method: 'POST', body: json(body) }),
  /** plata cu cardul: pagina Stripe, sau firma cu pachetul schimbat pe loc */
  payPlan: (body: { plan: PlanId; billing: Billing }) =>
    req<{ url: string } | { company: Company }>('/company/plan/checkout', {
      method: 'POST',
      body: json(body),
    }),
  /** portalul Stripe: facturi, card, anulare */
  billingPortal: () => req<{ url: string }>('/company/plan/portal', { method: 'POST' }),
  uploadLogo: (file: File) => {
    const body = new FormData();
    body.set('file', file);
    return req<Company>('/company/logo', { method: 'PUT', body });
  },
  deleteLogo: () => req<Company>('/company/logo', { method: 'DELETE' }),
  /** Adresa logo-ului, cu versiunea în ea: un logo nou are altă adresă, deci nu iese din cache. */
  logoUrl: (c: Pick<Company, 'logoAt'>): string | null =>
    c.logoAt ? `${BASE}/company/logo?v=${Date.parse(c.logoAt)}` : null,

  categories: () => req<{ used: string[]; suggested: string[] }>('/categories'),

  /** cererile deschise, pentru ecranul „Azi"; gruparea se face în browser */
  today: () => req<TodayRfq[]>('/today'),

  /** prețurile „ca la bursă”: pe material, cel mai mic acum și cine îl are */
  prices: (luni: PricePeriod = '12') => req<PriceMarket>(`/prices?luni=${luni}`),
  /** ce spune istoricul despre prețurile unei cereri, pe poziție */
  bench: (rfqId: string) => req<RfqBench>(`/prices/bench/${rfqId}`),
  /** cursul BNR al euro pentru o zi; fără zi, ultimul publicat */
  fxEur: (date?: string) => req<FxRate>(`/fx/eur${date ? `?date=${encodeURIComponent(date)}` : ''}`),
  /** toate prețurile unui material, cel mai recent primul */
  price: (key: string) => req<{ quotes: PriceQuote[] }>(`/prices/${encodeURIComponent(key)}`),

  sites: (opts: { includeClosed?: boolean } = {}) =>
    req<Site[]>(`/sites${opts.includeClosed ? '?includeClosed=1' : ''}`),
  site: (id: string) =>
    req<{
      site: Site;
      rfqs: RfqListItem[];
      ledger: SiteLedger;
      open: Rfq[];
      last: Rfq | null;
      events: ActivityEvent[];
      documents: SiteDocument[];
    }>(`/sites/${id}`),
  createSite: (body: SiteInput) => req<Site>('/sites', { method: 'POST', body: json(body) }),
  updateSite: (id: string, body: SitePatch) =>
    req<Site>(`/sites/${id}`, { method: 'PATCH', body: json(body) }),
  closeSite: (id: string) => req<Site>(`/sites/${id}/close`, { method: 'POST' }),
  reopenSite: (id: string) => req<Site>(`/sites/${id}/reopen`, { method: 'POST' }),
  deleteSite: (id: string) => req<{ deleted: true }>(`/sites/${id}`, { method: 'DELETE' }),

  suppliers: (opts: { category?: string; includeArchived?: boolean } = {}) => {
    const q = new URLSearchParams();
    if (opts.category) q.set('category', opts.category);
    if (opts.includeArchived) q.set('includeArchived', '1');
    const s = q.toString();
    return req<Supplier[]>(`/suppliers${s ? `?${s}` : ''}`);
  },
  importSuppliers: (suppliers: SupplierInput[]) =>
    req<{ created: number; skipped: number; noAddress?: number }>('/suppliers/import', {
      method: 'POST',
      body: json({ suppliers }),
    }),
  /** Datele firmei de la ANAF, pentru formular; nu salvează nimic. */
  anafLookup: (cui: string) => req<AnafInfo>(`/suppliers/anaf/${encodeURIComponent(cui)}`),
  recheckAnaf: (id: string) => req<Supplier>(`/suppliers/${id}/anaf`, { method: 'POST' }),
  createSupplier: (body: SupplierInput) => req<Supplier>('/suppliers', { method: 'POST', body: json(body) }),
  updateSupplier: (id: string, body: SupplierPatch) =>
    req<Supplier>(`/suppliers/${id}`, { method: 'PATCH', body: json(body) }),
  removeSupplier: (id: string) => req<SupplierRemoval>(`/suppliers/${id}`, { method: 'DELETE' }),
  restoreSupplier: (id: string) => req<Supplier>(`/suppliers/${id}/restore`, { method: 'POST' }),
  /** cum răspund furnizorii: rată, viteză, de câte ori au fost cei mai ieftini */
  scorecard: () => req<SupplierScore[]>('/suppliers/scorecard'),

  rfqs: (opts: RfqQuery = {}) => {
    const q = new URLSearchParams();
    if (opts.state) q.set('state', opts.state);
    if (opts.group) q.set('group', opts.group);
    if (opts.today) q.set('today', opts.today);
    if (opts.siteId) q.set('siteId', opts.siteId);
    if (opts.q) q.set('q', opts.q);
    if (opts.cursor) q.set('cursor', opts.cursor);
    if (opts.limit) q.set('limit', String(opts.limit));
    const s = q.toString();
    return req<RfqPage>(`/rfqs${s ? `?${s}` : ''}`);
  },
  dashboard: () => req<DashboardData>('/dashboard'),
  team: () => req<Team>('/team'),
  invite: (body: { email: string; role: string; isAdmin: boolean }) =>
    req<InviteCreated>('/team/invites', { method: 'POST', body: json(body) }),
  updateMember: (id: string, body: { role?: string; isAdmin?: boolean }) =>
    req<void>(`/team/users/${id}`, { method: 'PATCH', body: json(body) }),
  revokeInvite: (id: string) => req<void>(`/team/invites/${id}`, { method: 'DELETE' }),
  /** Șantierele de ale căror achiziții răspunde colegul; lista înlocuiește lista veche. */
  setMemberSites: (id: string, siteIds: string[]) =>
    req<{ siteIds: string[] }>(`/team/users/${id}/sites`, {
      method: 'PUT',
      body: json({ siteIds }),
    }),
  deactivateUser: (id: string) => req<void>(`/team/users/${id}/deactivate`, { method: 'POST' }),
  reactivateUser: (id: string) => req<void>(`/team/users/${id}/reactivate`, { method: 'POST' }),
  /** „Ajutor”: întrebările omului către echipa Procumi */
  supportList: () => req<MyTicket[]>('/support'),
  supportTicket: (id: string) => req<MyTicket>(`/support/${id}`),
  supportCreate: (body: { subject: string; category: string; body: string; files: File[] }) => {
    const form = new FormData();
    form.set('subject', body.subject);
    form.set('category', body.category);
    form.set('body', body.body);
    for (const f of body.files) form.append('files', f);
    return req<MyTicket>('/support', { method: 'POST', body: form });
  },
  supportReply: (id: string, body: string, files: File[]) => {
    const form = new FormData();
    form.set('body', body);
    for (const f of files) form.append('files', f);
    return req<MyTicket>(`/support/${id}/messages`, { method: 'POST', body: form });
  },
  supportSolved: (id: string) => req<MyTicket>(`/support/${id}/solved`, { method: 'POST' }),
  supportFileUrl: (id: string, messageId: string, index: number) =>
    `${BASE}/support/${id}/files/${messageId}/${index}`,
  inviteInfo: (token: string) => req<InviteInfo>(`/auth/invite/${encodeURIComponent(token)}`),
  acceptInvite: (body: { token: string; name: string; password: string }) =>
    req<Account>('/auth/accept-invite', { method: 'POST', body: json(body) }),

  rfq: (id: string) => req<Rfq>(`/rfqs/${id}`),
  createRfq: (body: RfqInput) => req<Rfq>('/rfqs', { method: 'POST', body: json(body) }),

  requisitions: () => req<{ rows: Requisition[]; groups: Record<ReqGroup, number> }>('/requisitions'),
  requisition: (id: string) => req<Requisition>(`/requisitions/${encodeURIComponent(id)}`),
  requisitionSites: () => req<SiteRef[]>('/requisitions/sites'),
  requisitionCategories: () => req<{ used: string[]; suggested: string[] }>('/requisitions/categories'),
  createRequisition: (body: RequisitionInput) =>
    req<Requisition>('/requisitions', { method: 'POST', body: json(body) }),
  updateRequisition: (id: string, body: RequisitionInput) =>
    req<Requisition>(`/requisitions/${encodeURIComponent(id)}`, { method: 'PUT', body: json(body) }),
  rejectRequisition: (id: string, reason: string) =>
    req<Requisition>(`/requisitions/${encodeURIComponent(id)}/reject`, {
      method: 'POST',
      body: json({ reason }),
    }),
  cancelRequisition: (id: string) =>
    req<Requisition>(`/requisitions/${encodeURIComponent(id)}/cancel`, { method: 'POST' }),
  reopenRequisition: (id: string) =>
    req<Requisition>(`/requisitions/${encodeURIComponent(id)}/reopen`, { method: 'POST' }),
  closeRfq: (id: string) => req<Rfq>(`/rfqs/${id}/close`, { method: 'POST' }),
  cancelRfq: (id: string) => req<Rfq>(`/rfqs/${id}/cancel`, { method: 'POST' }),
  reopenRfq: (id: string) => req<Rfq>(`/rfqs/${id}/reopen`, { method: 'POST' }),
  deleteRfq: (id: string) =>
    req<{ deleted: true; offers: number; files: number }>(`/rfqs/${id}`, { method: 'DELETE' }),
  chooseSupplier: (id: string, body: RfqChoiceInput) =>
    req<Rfq>(`/rfqs/${id}/choice`, { method: 'POST', body: json(body) }),
  clearChoice: (id: string) => req<Rfq>(`/rfqs/${id}/choice`, { method: 'DELETE' }),

  saveOffer: (rfqId: string, sendId: string, body: OfferInput) =>
    req<Rfq>(`/rfqs/${rfqId}/sends/${sendId}/offer`, { method: 'PUT', body: json(body) }),
  /** produsul echivalent acceptat, respins sau (null) iar nedecis (Ticket 10) */
  decideAlt: (rfqId: string, sendId: string, itemId: string, decision: AltDecision | null) =>
    req<Rfq>(`/rfqs/${rfqId}/sends/${sendId}/alt`, { method: 'POST', body: json({ itemId, decision }) }),

  /* poza de profil */
  uploadAvatar: (image: Blob) => {
    const body = new FormData();
    body.set('file', image, 'avatar.jpg');
    return req<{ avatarAt: string }>('/profile/avatar', { method: 'PUT', body });
  },
  setLocale: (locale: 'ro' | 'en') =>
    req<{ locale: 'ro' | 'en' }>('/profile/locale', { method: 'PATCH', body: json({ locale }) }),
  deleteAvatar: () => req<{ avatarAt: null }>('/profile/avatar', { method: 'DELETE' }),
  /** Schimbă parola; `ended` = câte alte sesiuni s-au închis. */
  changePassword: (current: string, next: string) =>
    req<{ ok: true; ended: number }>('/profile/password', { method: 'POST', body: json({ current, next }) }),
  endOtherSessions: () => req<{ ended: number }>('/profile/sessions/end-others', { method: 'POST' }),

  /* mailul de dimineață */
  setMorningMail: (on: boolean) =>
    req<{ morningMail: boolean }>('/profile/morning', { method: 'PATCH', body: json({ on }) }),
  setReportMail: (on: boolean) =>
    req<{ reportMail: boolean }>('/profile/report-mail', { method: 'PATCH', body: json({ on }) }),
  /** Semnătura omului; `dropped` = imaginile scoase (erau doar pe calculator). */
  saveSignature: (body: {
    mode: 'card' | 'outlook';
    title: string;
    phone: string;
    logo: boolean;
    html?: string;
  }) =>
    req<{ signature: UserSignature; dropped: number }>('/profile/signature', {
      method: 'PUT',
      body: json(body),
    }),
  /** Linkul „Nu mai vreau acest mail”: fără logare, doar cu tokenul din mail. */
  stopMorningMail: (token: string) =>
    req<{ ok: true }>(`/public/dimineata/${encodeURIComponent(token)}/stop`, { method: 'POST' }),

  /* clopoțelul */
  notifications: () => req<NotificationList>('/notifications'),
  /** Citite: cele date, sau toate. */
  readNotifications: (ids?: string[]) =>
    req<{ unread: number }>('/notifications/read', {
      method: 'POST',
      body: json(ids ? { ids } : {}),
    }),

  /* discuția echipei pe cerere */
  addComment: (rfqId: string, body: string) =>
    req<Rfq>(`/rfqs/${rfqId}/comments`, { method: 'POST', body: json({ body }) }),
  deleteComment: (rfqId: string, commentId: string) =>
    req<Rfq>(`/rfqs/${rfqId}/comments/${commentId}`, { method: 'DELETE' }),

  /* planurile cererii (shared/rfqFiles.ts) */
  uploadRfqFile: (rfqId: string, file: File) => {
    const body = new FormData();
    body.set('file', file);
    return req<Rfq>(`/rfqs/${rfqId}/files`, { method: 'POST', body });
  },
  rfqFileUrl: (rfqId: string, fileId: string) => `${BASE}/rfqs/${rfqId}/files/${fileId}`,
  deleteRfqFile: (rfqId: string, fileId: string) =>
    req<Rfq>(`/rfqs/${rfqId}/files/${fileId}`, { method: 'DELETE' }),

  /* documentele primite de la furnizor */
  uploadSendFile: (rfqId: string, sendId: string, file: File) => {
    const body = new FormData();
    body.set('file', file);
    return req<SendFileUpload>(`/rfqs/${rfqId}/sends/${sendId}/files`, { method: 'POST', body });
  },
  /**
   * Adresa de descărcare, pentru un `<a href>` obișnuit: sesiunea e în cookie,
   * iar serverul trimite numele original în `Content-Disposition`.
   */
  sendFileUrl: (rfqId: string, sendId: string, fileId: string) =>
    `${BASE}/rfqs/${rfqId}/sends/${sendId}/files/${fileId}`,
  deleteSendFile: (rfqId: string, sendId: string, fileId: string) =>
    req<Rfq>(`/rfqs/${rfqId}/sends/${sendId}/files/${fileId}`, { method: 'DELETE' }),
  /** Căsuța agentului: adresa firmei și mailurile primite; cu `rfqId`, doar ofertele pregătite pentru cerere. */
  inbox: (rfqId?: string) => req<InboxList>(rfqId ? `/inbox?rfqId=${encodeURIComponent(rfqId)}` : '/inbox'),
  /** Ce a citit agentul din mail, pentru formularul de ofertă. */
  inboxExtraction: (id: string) =>
    req<{ extraction: OfferExtraction | null; text: string; via: 'email' | 'link' }>(`/inbox/${id}/extraction`),
  inboxOptions: (id: string) => req<InboxOption[]>(`/inbox/${id}/options`),
  inboxAssign: (id: string, sendId: string) =>
    req<InboxMail>(`/inbox/${id}/assign`, { method: 'POST', body: json({ sendId }) }),
  inboxIgnore: (id: string, back = false) =>
    req<InboxMail>(`/inbox/${id}/ignore`, { method: 'POST', body: json({ back }) }),
  inboxFileUrl: (id: string, index: number) => `${BASE}/inbox/${id}/files/${index}`,
  /** Comanda rapidă: prețurile convenite (și cele expirate) și listele de comandă. */
  reorder: () =>
    req<{ today: string; prices: AgreedPrice[]; lists: OrderList[]; buyerReady: boolean }>('/reorder'),
  addAgreedPrices: (body: {
    supplierId: string;
    validUntil: string;
    category?: string;
    items: { desc: string; unit: string; unitPrice: number; minQty?: number | null }[];
  }) =>
    req<{ saved: number; prices: AgreedPrice[] }>('/reorder/prices', { method: 'POST', body: json(body) }),
  archiveAgreedPrice: (id: string) =>
    req<{ prices: AgreedPrice[] }>(`/reorder/prices/${id}/archive`, { method: 'POST' }),
  /** Prețurile câștigătoare ale cererii devin prețuri convenite. */
  keepPrices: (rfqId: string, validUntil?: string) =>
    req<{ saved: number; validUntil: string }>(`/reorder/from-rfq/${rfqId}`, {
      method: 'POST',
      body: json(validUntil ? { validUntil } : {}),
    }),
  saveOrderList: (
    id: string | null,
    body: {
      name: string;
      siteId: string | null;
      items: { desc: string; unit: string; qty: number | null }[];
    },
  ) =>
    req<{ id?: string; lists: OrderList[] }>(id ? `/reorder/lists/${id}` : '/reorder/lists', {
      method: id ? 'PUT' : 'POST',
      body: json(body),
    }),
  deleteOrderList: (id: string) => req<{ lists: OrderList[] }>(`/reorder/lists/${id}`, { method: 'DELETE' }),
  placeReorder: (body: {
    siteId: string;
    title: string;
    neededBy: string | null;
    lines: { desc: string; unit: string; qty: number }[];
  }) =>
    req<{
      orders: {
        rfqId: string;
        orderId: string;
        number: string;
        supplier: string;
        total: number;
        waiting: boolean;
      }[];
    }>('/reorder/order', { method: 'POST', body: json(body) }),
  /** Antemăsurătoarea citită automat: pozițiile, pe categorii; nu salvează nimic. */
  extractBoq: (src: { file: File } | { text: string }) => {
    if ('text' in src) return req<BoqExtraction>('/boq/extract', { method: 'POST', body: json(src) });
    const body = new FormData();
    body.set('file', src.file);
    return req<BoqExtraction>('/boq/extract', { method: 'POST', body });
  },
  /** Șablonul „Comparație oferte” completat → o cerere nouă, cu ofertele deja primite. */
  importTemplate: (o: { file: File; siteId: string; title: string; category: string }) => {
    const body = new FormData();
    body.set('file', o.file);
    body.set('siteId', o.siteId);
    body.set('title', o.title);
    body.set('category', o.category);
    return req<{ rfq: Rfq; newSuppliers: number; skipped: string[] }>('/rfqs/import-template', {
      method: 'POST',
      body,
    });
  },
  /** Citirea automată: propune prețuri, nu salvează nimic. */
  extractOffer: (rfqId: string, sendId: string, body: ExtractInput) =>
    req<OfferExtraction>(`/rfqs/${rfqId}/sends/${sendId}/extract`, {
      method: 'POST',
      body: json(body),
    }),
  markSent: (rfqId: string, sendId: string) =>
    req<Rfq>(`/rfqs/${rfqId}/sends/${sendId}/sent`, { method: 'POST' }),
  unmarkSent: (rfqId: string, sendId: string) =>
    req<Rfq>(`/rfqs/${rfqId}/sends/${sendId}/sent`, { method: 'DELETE' }),
  markAllSent: (rfqId: string) => req<Rfq>(`/rfqs/${rfqId}/sent`, { method: 'POST' }),
  /** trimite din Procumi: furnizorii aleși sau toți cei la care n-a plecat încă */
  mailRfq: (rfqId: string, sendIds?: string[]) =>
    req<{ rfq: Rfq; sent: number; failed: { supplier: string; error: string }[] }>(`/rfqs/${rfqId}/mail`, {
      method: 'POST',
      body: json(sendIds ? { sendIds } : {}),
    }),
  mailFollowUp: (rfqId: string, sendId: string) =>
    req<Rfq>(`/rfqs/${rfqId}/sends/${sendId}/follow-up/mail`, { method: 'POST' }),
  followUp: (rfqId: string, sendId: string) =>
    req<Rfq>(`/rfqs/${rfqId}/sends/${sendId}/follow-up`, { method: 'POST' }),

  /** Ciorna comenzii de achiziție, completată pe server, cu datele legale ale firmei. */
  orderDraft: (rfqId: string, kind: PoKind = 'order', supplierId?: string) => {
    const q = new URLSearchParams();
    if (kind === 'supplement') q.set('kind', 'supplement');
    // la comanda împărțită: partea furnizorului
    if (supplierId) q.set('supplier', supplierId);
    const qs = q.toString();
    return req<{ draft: PoDraft; company: Company }>(`/rfqs/${rfqId}/order-draft${qs ? `?${qs}` : ''}`);
  },
  /** `send`: o și trimite furnizorului din Procumi, dacă nu așteaptă aprobare. */
  issueOrder: (rfqId: string, body: PoInput, send = false) =>
    req<Rfq>(`/rfqs/${rfqId}/orders${send ? '?send=1' : ''}`, { method: 'POST', body: JSON.stringify(body) }),
  orders: () => req<OrderRow[]>('/orders'),
  approveOrder: (rfqId: string, orderId: string, comment: string) =>
    req<Rfq>(`/rfqs/${rfqId}/orders/${orderId}/approve`, {
      method: 'POST',
      body: json({ comment }),
    }),
  rejectOrder: (rfqId: string, orderId: string, comment: string) =>
    req<Rfq>(`/rfqs/${rfqId}/orders/${orderId}/reject`, {
      method: 'POST',
      body: json({ comment }),
    }),
  cancelOrder: (rfqId: string, orderId: string, reason: string) =>
    req<Rfq>(`/rfqs/${rfqId}/orders/${orderId}/cancel`, {
      method: 'POST',
      body: JSON.stringify({ reason }),
    }),
  /** „Livrată integral”: comanda iese din „în desfășurare”; `done=false` o readuce. */
  completeOrder: (rfqId: string, orderId: string, done: boolean) =>
    req<Rfq>(`/rfqs/${rfqId}/orders/${orderId}/complete`, { method: done ? 'POST' : 'DELETE' }),
  /** Trimite comanda furnizorului din Procumi (Echipă, Companie). */
  sendOrder: (rfqId: string, orderId: string) =>
    req<Rfq>(`/rfqs/${rfqId}/orders/${orderId}/send`, { method: 'POST' }),
  /** „Am trimis-o eu”, din programul de mail. */
  markOrderSent: (rfqId: string, orderId: string) =>
    req<Rfq>(`/rfqs/${rfqId}/orders/${orderId}/sent`, { method: 'POST' }),

  /* livrările pe comandă, cu avizele lor */
  createDelivery: (rfqId: string, orderId: string, body: DeliveryInput) =>
    req<Rfq>(`/rfqs/${rfqId}/orders/${orderId}/deliveries`, { method: 'POST', body: json(body) }),
  updateDelivery: (rfqId: string, orderId: string, deliveryId: string, body: DeliveryInput) =>
    req<Rfq>(`/rfqs/${rfqId}/orders/${orderId}/deliveries/${deliveryId}`, {
      method: 'PATCH',
      body: json(body),
    }),
  deleteDelivery: (rfqId: string, orderId: string, deliveryId: string) =>
    req<Rfq>(`/rfqs/${rfqId}/orders/${orderId}/deliveries/${deliveryId}`, { method: 'DELETE' }),
  uploadDeliveryFile: (rfqId: string, orderId: string, deliveryId: string, file: File) => {
    const body = new FormData();
    body.set('file', file);
    return req<Rfq>(`/rfqs/${rfqId}/orders/${orderId}/deliveries/${deliveryId}/files`, {
      method: 'POST',
      body,
    });
  },
  deleteDeliveryFile: (rfqId: string, orderId: string, deliveryId: string, fileId: string) =>
    req<Rfq>(`/rfqs/${rfqId}/orders/${orderId}/deliveries/${deliveryId}/files/${fileId}`, {
      method: 'DELETE',
    }),
  /** Pentru un `<a href>`: pozele și PDF-urile se deschid direct în browser. */
  deliveryFileUrl: (rfqId: string, orderId: string, deliveryId: string, fileId: string) =>
    `${BASE}/rfqs/${rfqId}/orders/${orderId}/deliveries/${deliveryId}/files/${fileId}`,
  /** Livrarea trecută de pe șantier, verificată de Achiziții. */
  confirmDelivery: (rfqId: string, orderId: string, deliveryId: string) =>
    req<Rfq>(`/rfqs/${rfqId}/orders/${orderId}/deliveries/${deliveryId}/confirm`, {
      method: 'POST',
    }),

  /* „Livrări”, în contul de șantier: comenzile fără prețuri */
  receiving: () => req<{ orders: SiteOrder[] }>('/receiving'),
  siteCreateDelivery: (orderId: string, body: DeliveryInput) =>
    req<SiteOrder>(`/receiving/${orderId}/deliveries`, { method: 'POST', body: json(body) }),
  siteUpdateDelivery: (orderId: string, deliveryId: string, body: DeliveryInput) =>
    req<SiteOrder>(`/receiving/${orderId}/deliveries/${deliveryId}`, {
      method: 'PATCH',
      body: json(body),
    }),
  siteDeleteDelivery: (orderId: string, deliveryId: string) =>
    req<SiteOrder>(`/receiving/${orderId}/deliveries/${deliveryId}`, { method: 'DELETE' }),
  siteUploadDeliveryFile: (orderId: string, deliveryId: string, file: File) => {
    const body = new FormData();
    body.set('file', file);
    return req<SiteOrder>(`/receiving/${orderId}/deliveries/${deliveryId}/files`, {
      method: 'POST',
      body,
    });
  },
  siteDeleteDeliveryFile: (orderId: string, deliveryId: string, fileId: string) =>
    req<SiteOrder>(`/receiving/${orderId}/deliveries/${deliveryId}/files/${fileId}`, {
      method: 'DELETE',
    }),
  siteDeliveryFileUrl: (orderId: string, deliveryId: string, fileId: string) =>
    `${BASE}/receiving/${orderId}/deliveries/${deliveryId}/files/${fileId}`,

  /** „Am întrebat”: întrebările despre ofertă au plecat din programul de mail */
  asked: (rfqId: string, sendId: string) =>
    req<Rfq>(`/rfqs/${rfqId}/sends/${sendId}/asked`, { method: 'POST' }),

  resetDemo: () => req<{ ok: true }>('/demo/reset', { method: 'POST' }),
};

export function errMsg(e: unknown): string {
  return e instanceof Error && e.message ? e.message : t('Ceva nu a funcționat.');
}
